import React, { useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/assetsLiabilities";
import { useAddAssetsLiabilities } from "app/hooks/assetsLiabilities/useAddAssetsLiabilities";
import { ASSST_LIABILITY } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";

import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import RadioButton from "app/components/common/buttons/RadioButton";
import AddMore from "app/components/common/buttons/AddMore";
import { useDeleteItem } from "app/hooks/useDeleteItem";
import DynamicExistingMortgage from "app/components/Individual/applicant/assetsLiabilities/DynamicExistingMortgage";
import { handleApiError } from "app/utils/handleApiError";

interface Inter {
  lenderName: string;
  balance: string;
  availableRedraw: string;
  minMonthlyPayment: string;
  dbId?: string;

  loanTerms: string;
  interestrate: string;
  noOfTitleHolders: string;
  propertyAddress: string;
  isInvestmentLoan: boolean;
  toBeRefinanced: boolean;
}
interface otherassetsInter {
  lenderName: string;
  balanceOwing: string;
  minMonthlyPayment: string;
  toBeRefinanced: boolean;
  dbId?: string;
}

export interface AssetsLiabilitiesPartThreeFormData {
  existingMortgageType?: string;
  carloanType?: string;
  existingMortgage?: Inter[];
  carLoan?: otherassetsInter[];
}

const PartThree: React.FC = () => {
  const { mutate: deleteItem, isPending: deleteIsPending } = useDeleteItem();
  const dispatch = useDispatch();
  const { mutate: submitAssetsLiabilities, isPending } =
    useAddAssetsLiabilities();
  const { formThree, assetLiabilityId, ...others } = useSelector(
    (state: RootState) => state.assetsLiabilities.stepsData
  ) || {
    formThree: { existingMortgage: [] },
  };
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    register,
    setValue,
    setError,
    clearErrors,
  } = useForm<AssetsLiabilitiesPartThreeFormData>({
    defaultValues: {
      existingMortgage: [
        {
          lenderName: "",
          balance: "",
          availableRedraw: "",
          minMonthlyPayment: "",
          loanTerms: "",
          interestrate: "",
          noOfTitleHolders: "",
          propertyAddress: "",
          isInvestmentLoan: false,
          toBeRefinanced: false,
        },
      ],
      existingMortgageType: "no",
    },
  });
  const existingmortgageValue = watch("existingMortgageType");

  const onSubmit = (data: AssetsLiabilitiesPartThreeFormData) => {
    if (!savedIds?.applicantID) {
      toast.error("Applicant id is required");
      return;
    }
    const { existingMortgage, existingMortgageType } = data || {};
    const payload: any = {};

    if (existingMortgageType === "yes") {
      payload.existingMortgage = existingMortgage?.map((item) => {
        const obj: any = {
          lenderName: item.lenderName,
          interestrate: item.interestrate,
          propertyAddress: item.propertyAddress,
          isInvestmentLoan: item.isInvestmentLoan,
          toBeRefinanced: item.toBeRefinanced,
          balance: Number(item.balance),
          availableRedraw: Number(item.availableRedraw),
          minMonthlyPayment: Number(item.minMonthlyPayment),
          loanTerms: Number(item.loanTerms),
          noOfTitleHolders: Number(item.noOfTitleHolders),
        };
        if (item.dbId) {
          obj.id = item.dbId;
        }
        return obj;
      });
    } else {
      payload.existingMortgage = null;
    }

    submitAssetsLiabilities(
      {
        url: `${ASSST_LIABILITY}/${savedIds?.applicantID}/third`,
        data: payload,
      },
      {
        onSuccess: (data: any) => {
          const { existingMortgage } = data || {};
          const existingMortgageMapped = existingMortgage?.map((item: any) => ({
            dbId: item.id,
            lenderName: item.lenderName,
            balance: item.balance,
            availableRedraw: item.availableRedraw,
            minMonthlyPayment: item.minMonthlyPayment,
            loanTerms: String(item.loanTerms),
            interestrate: item.interestrate,
            noOfTitleHolders: String(item.noOfTitleHolders),
            propertyAddress: item.propertyAddress,
            isInvestmentLoan: item.isInvestmentLoan,
            toBeRefinanced: item.toBeRefinanced,
          }));

          dispatch(
            addStepsData({
              ...others,
              formThree: {
                ...formThree,
                existingMortgage:
                  existingMortgageType === "no" ? [] : existingMortgageMapped,
                existingMortgageType,
              },
            })
          );

          dispatch(nextStep());
        },
        onError: (error: any) => {
          handleApiError(error);
        },
      }
    );
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "existingMortgage",
  });

  useEffect(() => {
    if (formThree && formThree?.existingMortgageType) {
      const updatedMortgage =
        formThree.existingMortgageType === "no"
          ? [
              {
                lenderName: "",
                balance: "",
                availableRedraw: "",
                minMonthlyPayment: "",
                loanTerms: "",
                interestrate: "",
                noOfTitleHolders: "",
                propertyAddress: "",
                isInvestmentLoan: false,
                toBeRefinanced: false,
              },
            ]
          : formThree.existingMortgage?.map((item) => ({
              ...item,
              loanTerms:
                item.loanTerms !== undefined && item.loanTerms !== null
                  ? String(item.loanTerms)
                  : "",
              noOfTitleHolders:
                item.noOfTitleHolders !== undefined &&
                item.noOfTitleHolders !== null
                  ? String(item.noOfTitleHolders)
                  : "",
            }));

      reset({
        ...(formThree.existingMortgageType && {
          existingMortgageType: formThree.existingMortgageType,
        }),
        existingMortgage: updatedMortgage,
      });
    }
  }, [formThree, reset]);

  return (
    <>
      <ProgressBar progress={24} MB={7} />
      <div className="w-full  sm:w-[80%] xl:w-[60%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Existing Mortgages" />
        <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
          <div className="text-left mb-5">
            <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
              Do you have any existing mortgage?
            </h6>
            <div className="flex gap-5">
              <Controller
                name="existingMortgageType"
                control={control}
                rules={{ required: "Please select an option" }}
                render={({ field: { value, onChange } }) => (
                  <div className="flex gap-5">
                    <RadioButton
                      id="existingmortgage_yes"
                      name="existingMortgageType"
                      value="yes"
                      label="Yes"
                      checked={value === "yes"}
                      onChange={onChange}
                    />
                    <RadioButton
                      id="existingmortgage_no"
                      name="existingMortgageType"
                      value="no"
                      label="No"
                      checked={value === "no"}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </div>
            {errors.existingMortgageType && (
              <ErrorsMessage
                title={errors.existingMortgageType.message}
                className="!text-left"
              />
            )}
          </div>

          {existingmortgageValue === "yes" && (
            <>
              <DynamicExistingMortgage
                control={control}
                errors={errors}
                register={register}
                setValue={setValue}
                setError={setError}
                clearErrors={clearErrors}
                fields={fields}
                deleteItem={deleteItem}
                existingMortgage="existingMortgage"
                applicantID={savedIds?.applicantID as string}
                remove={remove}
                deleteIsPending={deleteIsPending}
              />
              {fields.length < 3 && (
                <div className="flex items-center justify-end mt-6 mb-4 sm:mb-2">
                  <AddMore
                    onClick={() =>
                      append({
                        lenderName: "",
                        balance: "",
                        availableRedraw: "",
                        minMonthlyPayment: "",
                        loanTerms: "",
                        interestrate: "",
                        noOfTitleHolders: "",
                        propertyAddress: "",
                        isInvestmentLoan: false,
                        toBeRefinanced: false,
                      })
                    }
                  />
                </div>
              )}
            </>
          )}

          <div className="flex justify-between my-10">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartThree;
